import { Drawer } from "components";
import clsx from "clsx";
import { format } from "date-fns";
import moment from "moment";
import { useAppNavigate } from "_graphql/cache/auth";
import { useKavaNotifications } from "hooks/data/use-kava-notifications";

interface NotificationProps {
  open: boolean;
  setOpen: (val: boolean) => void;
}

export default function KavaNotificationsContainer({
  open,
  setOpen,
}: NotificationProps) {
  const navigate = useAppNavigate();
  const { kavaNotifications } = useKavaNotifications();

  const handleNotificationClick = (notification: any) => {
    navigate({
      to: `/create-slip/${notification?.offer?.offer_id}/view`,
      search: (prev) => ({
        ...prev,
        orderStatus: "participants",
        model: "chat",
      }),
    });
    setOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-600 text-yellow-100";
      case "OPEN":
        return "bg-green-600 text-green-100";
      default:
        return "bg-red-600 text-red-100";
    }
  };

  return (
    <Drawer
      open={open}
      setOpen={setOpen}
      size="lg"
      title="Kava Notifications"
      description="View and manage your notifications"
      hideActions
    >
      <div className="bg-white rounded-lg shadow-sm">
        <ul className="divide-y divide-gray-200">
          {kavaNotifications.map((notification, index) => (
            <li
              key={index}
              className="transition-colors bg-background-100 duration-150 ease-in-out hover:bg-gray-50 cursor-pointer"
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {notification?.offer?.offer_detail?.policy_number ||
                        "Unknown Policy"}
                    </span>
                    <span className="text-sm text-gray-500">
                      {notification?.offer?.offer_detail?.insured_by || "N/A"}
                    </span>
                    {Boolean(
                      notification?.offer?.thread_notification?.length
                    ) && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {notification?.offer?.thread_notification?.length}
                      </span>
                    )}
                  </div>
                  <span
                    className={clsx(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                      getStatusColor(notification?.offer?.offer_status || "")
                    )}
                  >
                    {notification?.offer?.offer_status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-900">
                      {notification?.offer?.classofbusiness?.business_name ||
                        "N/A"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {notification?.offer?.insurer?.insurer_company_name ||
                        "N/A"}
                    </p>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-sm text-gray-600">
                      {moment(
                        notification?.offer?.offer_detail
                          ?.period_of_insurance_from
                      ).format("MMM dd, YYYY")}{" "}
                      -{" "}
                      {moment(
                        notification?.offer?.offer_detail
                          ?.period_of_insurance_to
                      ).format("MMM dd, YYYY")}
                    </p>
                    <span
                      className={clsx(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                        notification?.offer?.payment_status === "PAID"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      )}
                    >
                      {notification?.offer?.payment_status}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Drawer>
  );
}
